
/*
 * Nettie's Native Purchase Controller v0.3
 *
 * Native PYO purchase flow:
 * - Validates flavor selections and packaging
 * - Checks Shopify product and variant
 * - Prevents duplicate submissions
 * - Shows loading and success feedback on ATC
 * - Submits one configured box to Shopify
 * - Verifies the resulting cart
 * - Redirects to the full cart
 *
 * Scope: Native products with Nettie's Box Builder.
 *
 * Security note:
 * Browser validation is not server-side enforcement.
 * Shopify checkout and fulfillment validation remain
 * separate launch requirements.
 */

(function () {
  'use strict';

  const VERSION = '0.3';

  let purchaseInProgress = false;
  let purchaseCompleted = false;

  const BUTTON_STYLE_ID = 'netties-purchase-button-styles';

  /*
   * Button states
   *
   * Uses the existing Broadcast button and preserves
   * its sizing, color, and normal appearance.
   *
   * Only native PYO buttons receive these styles.
   */

  function installButtonStyles() {
    if (document.getElementById(BUTTON_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = BUTTON_STYLE_ID;

    style.textContent = `
      button[data-add-to-cart].netties-atc--loading,
      button[data-add-to-cart].netties-atc--success {
        cursor: wait;
      }

      button[data-add-to-cart].netties-atc--loading
      .btn__text,
      button[data-add-to-cart].netties-atc--success
      .btn__text {
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        gap: 10px;
        opacity: 1 !important;
        visibility: visible !important;
      }

      button[data-add-to-cart].netties-atc--loading
      .btn__loader,
      button[data-add-to-cart].netties-atc--success
      .btn__loader,
      button[data-add-to-cart].netties-atc--loading
      .btn__added,
      button[data-add-to-cart].netties-atc--success
      .btn__added {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }

      .netties-atc__spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
        box-sizing: border-box;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: netties-atc-spin 650ms linear infinite;
      }

      .netties-atc__check {
        display: inline-block;
        font-size: 18px;
        font-weight: 700;
        line-height: 1;
      }

      @keyframes netties-atc-spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .netties-atc__spinner {
          animation: none;
          border-right-color: currentColor;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function isSubmitting() {
    return purchaseInProgress || purchaseCompleted;
  }

  function getBuilderForm() {
    const state = window.nettiesBoxBuilderPOC?.getState();

    return state?.formId
      ? document.getElementById(state.formId)
      : null;
  }

  function getAddButton() {
    return getBuilderForm()?.querySelector(
      'button[data-add-to-cart]'
    ) || null;
  }

  function getStatusElement() {
    const builderRoot = document.querySelector(
      '[data-nbb-builder]'
    );

    return builderRoot?.querySelector('.nbb-status') || null;
  }

  function setStatus(message) {
    const element = getStatusElement();

    if (element) {
      element.textContent = message || '';
    }
  }

  /*
   * Save the normal ATC label once.
   * Restore it after an unsuccessful submission.
   */

  function rememberButtonLabel(button) {
    if (!button) return;

    if (!button.hasAttribute('data-netties-atc-original')) {
      const label = button.querySelector('.btn__text');

      button.setAttribute(
        'data-netties-atc-original',
        label?.textContent.trim() || 'Add to cart'
      );
    }
  }

  function setButtonState(state) {
    const button = getAddButton();

    if (!button) return;

    rememberButtonLabel(button);
    installButtonStyles();

    const label = button.querySelector('.btn__text');

    if (!label) return;

    button.classList.remove(
      'netties-atc--loading',
      'netties-atc--success'
    );

    label.replaceChildren();

    if (state === 'loading') {
      button.classList.add('netties-atc--loading');

      const spinner = document.createElement('span');
      spinner.className = 'netties-atc__spinner';
      spinner.setAttribute('aria-hidden', 'true');

      label.append(
        spinner,
        document.createTextNode('Adding to cart…')
      );

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');

      return;
    }

    if (state === 'success') {
      button.classList.add('netties-atc--success');

      const check = document.createElement('span');
      check.className = 'netties-atc__check';
      check.textContent = '✓';
      check.setAttribute('aria-hidden', 'true');

      label.append(
        check,
        document.createTextNode('Added to cart')
      );

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.removeAttribute('aria-busy');

      return;
    }

    label.textContent =
      button.getAttribute('data-netties-atc-original') ||
      'Add to cart';

    button.removeAttribute('aria-busy');

    // The Box Builder decides whether purchasing
    // should now be enabled.
    window.nettiesBoxBuilderPOC?.refresh();
  }

  /*
   * Validation
   */

  function preview() {
    const errors = [];
    const warnings = [];

    const builderApi = window.nettiesBoxBuilderPOC;
    const packagingApi = window.nettiesPackagingPOC;

    const builder = builderApi?.getState();
    const packaging = packagingApi?.getState();

    if (!builder) {
      errors.push('Box Builder is not initialized.');
    }

    if (!packaging) {
      errors.push('Packaging selector is not initialized.');
    }

    const formId = builder?.formId;

    const form = formId
      ? document.getElementById(formId)
      : null;

    if (!form) {
      errors.push('Product form not found.');
    }

    if (
      builder?.formId &&
      packaging?.formId &&
      builder.formId !== packaging.formId
    ) {
      errors.push(
        'Builder and packaging use different forms.'
      );
    }

    const required = builder?.required;
    const selected = builder?.selected;

    if (
      !Number.isInteger(required) ||
      ![15, 30].includes(required)
    ) {
      errors.push('Unsupported box size.');
    }

    if (
      !Number.isInteger(selected) ||
      selected !== required ||
      builder?.valid !== true
    ) {
      errors.push(
        'The box does not contain the required number of bites.'
      );
    }

    const selections = Array.isArray(builder?.selections)
      ? builder.selections
      : [];

    const allowedIds = new Set(
      builder?.allowedFlavorIds || []
    );

    const selectedIds = new Set();

    let selectionTotal = 0;

    for (const flavor of selections) {
      const id = flavor.id;
      const quantity = flavor.quantity;

      if (
        typeof id !== 'string' ||
        !id ||
        id.includes('|') ||
        id.includes(',') ||
        !allowedIds.has(id) ||
        selectedIds.has(id) ||
        !Number.isInteger(quantity) ||
        quantity <= 0 ||
        quantity > required
      ) {
        errors.push(
          'Invalid or duplicate flavor selection.'
        );
        break;
      }

      selectedIds.add(id);
      selectionTotal += quantity;
    }

    if (selectionTotal !== required) {
      errors.push(
        'Flavor quantities do not match the box size.'
      );
    }

    const formData = form
      ? new FormData(form)
      : new FormData();

    const propertyName =
      'Select ' + required + ' Flavors';

    const flavorKey =
      'properties[' + propertyName + ']';

    const expectedFlavorValue = selections
      .map(flavor =>
        flavor.id + '|' + flavor.quantity
      )
      .join(',');

    const actualFlavorValue =
      formData.get(flavorKey);

    if (
      !actualFlavorValue ||
      actualFlavorValue !== expectedFlavorValue
    ) {
      errors.push(
        'Flavor order property is missing or inconsistent.'
      );
    }

    if (
      builder?.propertyName !== propertyName ||
      builder?.propertyValue !== expectedFlavorValue
    ) {
      errors.push('Builder state is inconsistent.');
    }

    /*
     * Packaging validation
     */

    if (packaging?.fulfillmentMode !== 'property') {
      errors.push(
        'Custom box packaging must use property mode.'
      );
    }

    const sleeveCode =
      packaging?.sleeve || '';

    const packagingName =
      formData.get('properties[Packaging]');

    const packagingCode =
      formData.get('properties[Packaging Code]');

    if (
      typeof packagingName !== 'string' ||
      !packagingName
    ) {
      errors.push('Packaging selection is missing.');
    }

    if (
      packagingCode !== (sleeveCode || 'STANDARD')
    ) {
      errors.push(
        'Packaging code does not match the selected sleeve.'
      );
    }

    if (
      !sleeveCode &&
      packagingName !== 'Standard'
    ) {
      errors.push(
        'Standard packaging name is inconsistent.'
      );
    }

    /*
     * Shopify variant validation
     */

    const variantId = String(
      packaging?.base?.id || ''
    );

    const formVariantId = String(
      formData.get('id') || ''
    );

    if (
      !variantId ||
      !formVariantId ||
      variantId !== formVariantId
    ) {
      errors.push('Shopify variant does not match.');
    }

    if (
      packaging?.target?.id &&
      String(packaging.target.id) !== variantId
    ) {
      errors.push(
        'Custom packaging cannot change the base variant.'
      );
    }

    const sku =
      packaging?.base?.sku || '';

    const expectedSku =
      required === 15 ? 'CUSTOM15' : 'CUSTOM30';

    if (sku !== expectedSku) {
      errors.push(
        'Product SKU does not match the box size.'
      );
    }

    if (packaging?.base?.available === false) {
      errors.push('Product variant is unavailable.');
    }

    /*
     * Number of complete boxes.
     *
     * This is separate from flavors per box.
     */

    const rawQuantity =
      formData.get('quantity');

    const quantity = rawQuantity === null
      ? 1
      : Number(rawQuantity);

    if (
      !Number.isInteger(quantity) ||
      quantity < 1
    ) {
      errors.push('Invalid number of boxes.');
    }

    /*
     * Collect and validate order properties.
     */

    const properties = {};
    const propertyCounts = {};

    for (const [key, value] of formData.entries()) {
      const match =
        key.match(/^properties\[(.+)\]$/);

      if (!match) continue;

      const name = match[1];

      propertyCounts[name] =
        (propertyCounts[name] || 0) + 1;

      properties[name] = String(value);
    }

    const duplicates = Object.keys(
      propertyCounts
    ).filter(
      name => propertyCounts[name] !== 1
    );

    if (duplicates.length) {
      errors.push(
        'Duplicate order properties: ' +
        duplicates.join(', ')
      );
    }

    const expectedNames = [
      propertyName,
      'Packaging',
      'Packaging Code'
    ];

    const unexpected = Object.keys(
      properties
    ).filter(
      name => !expectedNames.includes(name)
    );

    if (unexpected.length) {
      errors.push(
        'Unexpected order properties: ' +
        unexpected.join(', ')
      );
    }

    for (const name of expectedNames) {
      if (propertyCounts[name] !== 1) {
        errors.push(
          'Missing or duplicate property: ' + name
        );
      }
    }

    const payload = {
      items: [{
        id: variantId,
        quantity:
          Number.isInteger(quantity) && quantity > 0
            ? quantity
            : 1,
        properties
      }]
    };

    return {
      version: VERSION,
      valid: errors.length === 0,
      sku,
      box: {
        required,
        selected,
        selectionTotal
      },
      packaging: {
        name: packagingName,
        code: packagingCode
      },
      payload,
      errors,
      warnings
    };
  }

  /*
   * Shopify AJAX requests
   */

  async function submitValidatedItem(validation) {
    const rootUrl =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      rootUrl + 'cart/add.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(validation.payload)
      }
    );

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(
        result.description ||
        result.message ||
        'Shopify rejected the cart item.'
      );
    }

    return result;
  }

  async function verifyCart(validation) {
    const rootUrl =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      rootUrl + 'cart.js',
      {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Could not verify the Shopify cart.'
      );
    }

    const cart = await response.json();

    const expected =
      validation.payload.items[0];

    const matchingLines = cart.items.filter(item =>
      String(item.variant_id) === String(expected.id) &&
      Object.entries(expected.properties).every(
        ([key, value]) =>
          String(item.properties?.[key] ?? '') ===
          String(value)
      )
    );

    const matchingQuantity =
      matchingLines.reduce(
        (total, item) => total + item.quantity,
        0
      );

    return {
      verified:
        matchingQuantity >= expected.quantity,
      matchingQuantity,
      requestedQuantity: expected.quantity,
      matchingLines,
      cartItemCount: cart.item_count
    };
  }

  /*
   * Explicit console testing API.
   *
   * Never redirects. Only adds to cart when called
   * with the exact confirmation phrase.
   */

  async function addTestItem(confirmation) {
    if (confirmation !== 'ADD TEST ITEM') {
      console.warn(
        '[Netties Purchase] Confirmation required.'
      );
      return null;
    }

    if (isSubmitting()) {
      console.warn(
        '[Netties Purchase] Purchase already in progress.'
      );
      return null;
    }

    const validation = preview();

    if (!validation.valid) {
      console.error(
        '[Netties Purchase] Validation failed:',
        validation.errors
      );

      return {
        verified: false,
        errors: validation.errors
      };
    }

    purchaseInProgress = true;
    setButtonState('loading');
    setStatus('');

    let added = false;

    try {
      await submitValidatedItem(validation);
      added = true;

      const verification =
        await verifyCart(validation);

      console.info(
        '[Netties Purchase] Test verification:',
        verification
      );

      return {
        added: true,
        ...verification
      };
    } catch (error) {
      console.error(
        '[Netties Purchase] Cart test failed:',
        error
      );

      return {
        added,
        verified: false,
        error: error.message,
        checkCartBeforeRetrying: added
      };
    } finally {
      purchaseInProgress = false;
      setButtonState('ready');
    }
  }

  /*
   * Normal customer purchase flow.
   */

  async function purchase() {
    if (isSubmitting()) return;

    const validation = preview();

    if (!validation.valid) {
      console.error(
        '[Netties Purchase] Purchase blocked:',
        validation.errors
      );

      setStatus(
        'Unable to add your box. ' +
        'Please refresh and try again.'
      );

      return;
    }

    purchaseInProgress = true;

    setStatus('');
    setButtonState('loading');

    let added = false;
    let completed = false;

    try {
      await submitValidatedItem(validation);

      added = true;

      const verification =
        await verifyCart(validation);

      if (!verification.verified) {
        throw new Error(
          'The box was added, but cart verification failed.'
        );
      }

      completed = true;
      purchaseCompleted = true;

      setButtonState('success');

      /*
       * Brief success state before navigating.
       * The cart has already been verified.
       */

      await new Promise(resolve =>
        window.setTimeout(resolve, 450)
      );

      const rootUrl =
        window.Shopify?.routes?.root || '/';

      window.location.assign(rootUrl + 'cart');

    } catch (error) {
      console.error(
        '[Netties Purchase] Purchase failed:',
        error
      );

      if (added) {
        setStatus(
          'Your box may already be in the cart. ' +
          'Please check your cart before trying again.'
        );
      } else {
        setStatus(
          'Unable to add your box. Please try again.'
        );
      }

    } finally {
      purchaseInProgress = false;

      if (!completed) {
        setButtonState('ready');
      }
    }
  }

  /*
   * Intercept only the native PYO product form.
   *
   * Normal products continue using Broadcast.
   */

  function handlePurchaseClick(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(
      'button[data-add-to-cart]'
    );

    if (!button) return;

    const form = getBuilderForm();

    if (!form || button.form !== form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.disabled || isSubmitting()) return;

    purchase();
  }

  function handlePurchaseSubmit(event) {
    const form = getBuilderForm();

    if (!form || event.target !== form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (isSubmitting()) return;

    purchase();
  }

  document.addEventListener(
    'click',
    handlePurchaseClick,
    true
  );

  document.addEventListener(
    'submit',
    handlePurchaseSubmit,
    true
  );

  /*
   * Public debugging / testing interface.
   */

  window.nettiesPurchaseTest = {
    version: VERSION,
    dryRun: false,
    purchaseEnabled: true,
    isSubmitting,
    preview,
    addTestItem,
    purchase
  };

  // Refresh the builder now that the controller is ready.
  window.nettiesBoxBuilderPOC?.refresh();

  console.info(
    '[Netties Purchase] v0.3 initialized'
  );

  // Safari can restore the product page from its
  // back/forward cache after navigating to the cart.
  // Reset the completed purchase state only when
  // returning to a previously cached page.
  window.addEventListener('pageshow', function (event) {

    if (!event.persisted) return;

    purchaseInProgress = false;
    purchaseCompleted = false;

    // Restore the normal Add to Cart appearance.
    // setButtonState('ready') also refreshes the
    // builder and rechecks the selected flavors.
    setButtonState('ready');

    console.info(
      '[Netties Purchase] Restored after browser Back'
    );

  });

})();
